import React, { useRef, useEffect } from 'react';
import { getImageRect, fitCanvasToParent, observeSize, observePixelRatio } from './CanvasUtil';

function Canvas({ img }) {
    const canRef = useRef(); // canvas
    const ctxRef = useRef(); // canvas 2d context

    useEffect(() => {
        ctxRef.current = canRef.current.getContext('2d');
    }, [canRef]);

    const drawImg = () => {
        if (!img) return;

        const canvasWidth = canRef.current.width;
        const canvasHeight = canRef.current.height;
        const { x, y, width, height } = getImageRect(img, canvasWidth, canvasHeight);

        ctxRef.current.clearRect(0, 0, canvasWidth, canvasHeight);
        // The browser's default smoothing is cheap and blurs a slide noticeably
        // whenever it is drawn smaller than it was rendered.
        ctxRef.current.imageSmoothingQuality = 'high';
        ctxRef.current.drawImage(img, x, y, width, height);
    };

    const update = () => {
        if (!img) return;

        // The image layer alone is drawn at device pixels, so a HiDPI screen
        // shows the slide at its full resolution rather than upscaling a
        // CSS-pixel bitmap. Nothing reads coordinates off this layer, so its
        // bitmap needn't match the drawing layer above it.
        fitCanvasToParent(canRef.current, window.devicePixelRatio || 1);
        drawImg();
    };

    // Re-fit from the container's own size rather than a window resize, so the
    // image layer stays in step with the drawing layer above it for every
    // layout change, not just the ones the window happens to see - and when
    // the pixel ratio changes, which leaves the container's size alone.
    useEffect(() => {
        if (!img) return undefined;

        update();
        const stopSize = observeSize(canRef.current.parentElement, update);
        const stopRatio = observePixelRatio(update);
        return () => {
            stopSize();
            stopRatio();
        };
    }, [img]);

    return (
        <canvas
            style={{
                position: 'absolute'
            }}
            ref={canRef}
        />
    );
}

export default Canvas;
